import { useEffect, useRef, useCallback } from 'react';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import filterChildren from "../../utils/Children/filterChildren";
import Icon from "../Icon/Icon";
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { ESC_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getHeight(topOffset) {
  return {
    height: `calc(100% - ${topOffset})`
  };
}
function Drawer(_ref) {
  let {
    id = '',
    className = '',
    children = [],
    open,
    secondary = false,
    hideCloseButton = false,
    style = {},
    title = null,
    topOffset = '0',
    width = '20%',
    theme = 'light',
    onClose = noop,
    onEscClick = undefined,
    ctaBtnRef = null
  } = _ref;
  const drawerRef = useRef(null);
  const firstFocusableElement = useRef(null);
  const lastFocusableElement = useRef(null);
  const handleEscClick = useCallback(e => {
    if (isKeyPressed(e, 'Escape', ESC_KEY_CODE) && open) {
      const closeFn = onEscClick || onClose;
      closeFn();
      if (ctaBtnRef) {
        ctaBtnRef.current?.focus();
      }
    }
  }, [open, onEscClick, onClose, ctaBtnRef]);
  useEffect(() => {
    const el = drawerRef.current;
    if (!open || !el) return;
    el.addEventListener('keydown', handleEscClick, false);
    return () => {
      el.removeEventListener('keydown', handleEscClick);
    };
  }, [handleEscClick, open]);
  useEffect(() => {
    if (open && drawerRef.current) {
      drawerRef.current.focus();
      trapFocusInDrawer(drawerRef.current);
    }
  }, [open]);
  const handleClose = useCallback(() => {
    if (open) {
      onClose();
      if (ctaBtnRef) {
        ctaBtnRef.current?.focus();
      }
    }
  }, [open, onClose, ctaBtnRef]);
  const drawerClass = cn('fixed top-0 h-full transition-transform duration-500 z-drawer', {
    'left-0 transform': !secondary,
    '-translate-x-full': !open && !secondary,
    'right-0 transform': secondary,
    'translate-x-full': !open && secondary,
    'translate-x-0': open,
    'bg-white': !secondary
  }, className);
  const buttonClass = cn('absolute top-2 transform h-8 w-8 rounded-full border border-gray-300 bg-white transition-transform duration-750 z-modal flex items-center justify-center mt-2 cursor-pointer', {
    'left-full ml-2 -translate-x-0': !secondary && open,
    'right-full mr-2 translate-x-0': secondary && open,
    '-translate-x-full': !open && !secondary,
    'translate-x-full': !open && secondary,
    'bg-gray-800': theme === 'dark'
  });
  const newChildren = filterChildren(children);
  const trapFocusInDrawer = modalElement => {
    const focusableElements = modalElement.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    firstFocusableElement.current = focusableElements[0];
    lastFocusableElement.current = focusableElements[focusableElements.length - 1];
    modalElement.addEventListener('keydown', event => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          // Shift+Tab
          if (document.activeElement === firstFocusableElement.current) {
            lastFocusableElement.current.focus();
            event.preventDefault();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusableElement.current) {
            firstFocusableElement.current.focus();
            event.preventDefault();
          }
        }
      }
    });
  };
  return /*#__PURE__*/_jsx("div", {
    ref: drawerRef,
    id: id,
    className: drawerClass,
    style: {
      ...style,
      top: topOffset,
      width,
      ...getHeight(topOffset),
      display: `${open ? 'block' : 'none'}`
    },
    tabIndex: -1,
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": title ? `${id}-title` : undefined,
    "aria-hidden": !open,
    children: /*#__PURE__*/_jsxs("div", {
      className: "relative h-full",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "absolute z-drawer h-full w-full bg-white shadow-xl overflow-y-auto overflow-x-hidden",
        children: [/*#__PURE__*/_jsx("div", {
          className: "flex flex-row items-start",
          children: title && /*#__PURE__*/_jsx("div", {
            className: "flex-grow p-2",
            children: title
          })
        }), newChildren]
      }), !hideCloseButton && /*#__PURE__*/_jsx("button", {
        className: buttonClass,
        onClick: handleClose,
        role: "button",
        children: /*#__PURE__*/_jsx(Icon, {
          ariaLabel: "Close",
          icon: "Cross",
          size: 14,
          color: theme === 'dark' ? 'white' : 'medium-border-blue'
        })
      })]
    })
  });
}
Drawer.propTypes = {
  /** id for reference */
  id: PropTypes.string,
  /** className for reference */
  className: PropTypes.string,
  /** If true, drawer hides close button.  Defaults to false. */
  hideCloseButton: PropTypes.bool,
  /** When close button is clicked or Esc key pressed, run this function */
  onClose: PropTypes.func,
  /** When Esc key is pressed, run this function. Takes precedence over onClose function. */
  onEscClick: PropTypes.func,
  /** If true, the Drawer is open. */
  open: PropTypes.bool.isRequired,
  /** If true, the Drawer is positioned to open from the opposite side. */
  secondary: PropTypes.bool,
  /** Override the inline-styles of the root element || Not Recommended */
  style: PropTypes.object,
  /** The drawer title string or node */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** How much to offset drawer from top of window */
  topOffset: PropTypes.string,
  /** The width of the Drawer in pixels or percentage in string format ex. 50% to fill half of the window or 100% and so on. */
  width: PropTypes.string,
  /**
   * children props for content inside
   * @ignore
   */
  children: PropTypes.node,
  /** theme to determine light or dark mode */
  theme: PropTypes.oneOf(["light", "dark"]),
  /** Ref to the call to action button that should regain focus when drawer closes */
  ctaBtnRef: PropTypes.object
};
export default Drawer;
//# sourceMappingURL=Drawer.js.map